import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartArea } from '@fortawesome/pro-duotone-svg-icons/faChartArea'
import { faStar } from '@fortawesome/pro-duotone-svg-icons/faStar'
import { faUserNinja } from '@fortawesome/pro-duotone-svg-icons/faUserNinja'
import { faUserTie } from '@fortawesome/pro-duotone-svg-icons/faUserTie'
import { faUserCog } from '@fortawesome/pro-duotone-svg-icons/faUserCog'
import { faUser } from '@fortawesome/pro-duotone-svg-icons/faUser'

import { useTheme, lighten } from '@mui/material/styles'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

import { Redirect, useHistory } from 'react-router-dom'

import { MessagesPopOver } from '@/feature/user/messages'
import { UpperMenuQuickSearch } from '@/feature/navigation/Topmenu'
import { UpperMenuLocales } from '@/feature/navigation/Topmenu'
import { UserProfile } from '@/feature/navigation/Topmenu'
import { CompactButton } from '@/shared/ui/buttons'

import { UpperMenuProps } from '../model/types'
import { useLogout } from '../hooks/useLogout'
import MenuBars from '../../../../images/bars.svg'

const UpperMenu: React.FC<UpperMenuProps> = ({
    kedo,
    handleSidebar,
    onMenuOpen,
    menuOpen = false,
    isMobile = false,
    drawerWidth = 280,
}) => {
    const history = useHistory()
    const theme = useTheme()

    const [redirect, setRedirect] = useState(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleLogoutClick = useLogout(kedo, setRedirect)

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }

    const handleCloseMenu = () => setAnchorEl(null)

    const getUserIcon = () => {
        if (kedo.isAdmin()) return faUserNinja
        if (kedo.isContractAdmin()) return faUserTie
        if (kedo.isEnvironmentAdmin()) return faUserCog
        return faUser
    }

    if (redirect) {
        return <Redirect to="/" />
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar
                data-cy="menu-appbar"
                position="fixed"
                sx={{
                    zIndex: theme.zIndex.drawer - 1,
                    px: theme.spacing(3),
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    ...(isMobile
                        ? {
                              width: '100%',
                              marginLeft: 0,
                          }
                        : menuOpen
                        ? {
                              width: `calc(100% - ${drawerWidth}px)`,
                              marginLeft: `${drawerWidth}px`,
                              transition: theme.transitions.create(
                                  ['margin', 'width'],
                                  {
                                      easing: theme.transitions.easing.easeOut,
                                      duration:
                                          theme.transitions.duration
                                              .enteringScreen,
                                  }
                              ),
                          }
                        : {
                              width: '100%',
                              marginLeft: 0,
                          }),
                }}
            >
                <Toolbar
                    sx={{
                        minHeight: 60,
                        '@media (min-width: 600px)': { minHeight: 60 },
                    }}
                >
                    {kedo.env()?.hasEnviroment() && (
                        <CompactButton
                            data-cy="menu-open-button"
                            onClick={onMenuOpen}
                            title={kedo.t('Open menu')}
                            sx={[
                                { mr: 2, position: 'relative' },
                                menuOpen && { display: 'none' },
                            ]}
                        >
                            <img
                                src={MenuBars}
                                alt="Open menu"
                                style={{ width: 20, height: 20 }}
                            />
                        </CompactButton>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            {kedo.env() &&
                                kedo.env().hasEnviroment() &&
                                (kedo.isAdminOrEnvironmentAdmin() ||
                                    kedo
                                        .env()
                                        .hasEntityCredentials(
                                            'quick_search'
                                        )) && (
                                    <UpperMenuQuickSearch kedo={kedo} />
                                )}

                            <MessagesPopOver kedo={kedo} />
                            <UpperMenuLocales kedo={kedo} />

                            {kedo.env() &&
                                kedo.env().hasEnviroment() &&
                                (kedo.isAdminOrEnvironmentAdmin() ||
                                    kedo.env().hasCredential('export')) &&
                                kedo.env().hasModule('reports') && (
                                    <IconButton
                                        size="large"
                                        aria-label="reports overview"
                                        onClick={() =>
                                            history.push('/reports/overview')
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={faChartArea}
                                            style={{ fontSize: 16 }}
                                        />
                                    </IconButton>
                                )}

                            {kedo.env() && kedo.env().hasEnviroment() && (
                                <IconButton
                                    data-cy="favorites-open-button"
                                    size="large"
                                    aria-label="favorites"
                                    onClick={handleSidebar}
                                >
                                    <FontAwesomeIcon
                                        icon={faStar}
                                        style={{ fontSize: 16 }}
                                    />
                                </IconButton>
                            )}
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                position: 'relative',
                                ml: 0.5,
                                px: 1,
                                borderRadius: 1,
                                backgroundColor: lighten(
                                    theme.palette.primary.light,
                                    0.9
                                ),
                                '&:hover': {
                                    backgroundColor: lighten(
                                        theme.palette.primary.light,
                                        0.8
                                    ),
                                },
                                '&::after': {
                                    content: '""',
                                    display: 'inline-block',
                                    marginLeft: '6px',
                                    verticalAlign: 'middle',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '4px solid transparent',
                                    borderRight: '4px solid transparent',
                                    borderTop: '5px solid currentColor',
                                },
                            }}
                            onClick={handleOpenMenu}
                        >
                            <Typography>
                                {kedo.user().getUsername() ?? '?'}
                            </Typography>
                        </Box>

                        <UserProfile
                            anchorEl={anchorEl}
                            onClose={handleCloseMenu}
                            handleLogoutClick={handleLogoutClick}
                            kedo={kedo}
                        />
                    </Box>
                </Toolbar>
            </AppBar>
        </Box>
    )
}

export default UpperMenu
